/**
 * Pure labels behind the Production Records timeline and its wizards.
 *
 *   - the timeline's day labels (TODAY / YESTERDAY / THU · SEP 10, and the
 *     "Thu, Sep 10" a card title uses) come from one place;
 *   - the wizard header's second line names the campus and the service day
 *     the task refers to (`taskContextLabel`);
 *   - the two shipped one-step tasks keep their titles and URL param
 *     (`DAY_TASK_TITLE`, `parseDayTaskParam`): the Tasks inbox and
 *     notifications still emit those deep links, which the page rewrites to
 *     the unified record.
 *
 * What a day's card SHOWS (its parts, percent and one action) lives in
 * `day-progress.ts`. Side-effect free; unit-tested in
 * `client/test/lib/production-records/day-tasks.test.ts`.
 */

export type DayTaskKind = 'transport-logs' | 'service-records'

export const DAY_TASK_TITLE: Record<DayTaskKind, string> = {
  'transport-logs': 'Transport logs',
  'service-records': 'Service records',
}

export function parseDayTaskParam(raw: string | null | undefined): DayTaskKind | null {
  return raw === 'transport-logs' || raw === 'service-records' ? raw : null
}

function localDate(iso: string): Date | null {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) {
    return null
  }
  return new Date(y, m - 1, d)
}

function isoOf(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** "Thu, Sep 10" for a yyyy-mm-dd (local calendar day, zone-safe). */
export function formatTaskDate(iso: string): string {
  const date = localDate(iso)
  if (!date) {
    return iso
  }
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

/** "Today" / "Yesterday" for the two days a card names rather than dates; null for every other day. */
export function relativeDayLabel(iso: string, now: Date = new Date()): 'Today' | 'Yesterday' | null {
  const today = isoOf(now)
  if (iso === today) {
    return 'Today'
  }
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  if (iso === isoOf(yesterday)) {
    return 'Yesterday'
  }
  return null
}

/**
 * Timeline day header: TODAY / YESTERDAY, else the uppercase middot form
 * (`THU · SEP 10`) the shared event timeline uses, so the page reads like
 * the rest of the Ordo timelines.
 */
export function formatTimelineDayLabel(iso: string, now: Date = new Date()): string {
  const relative = relativeDayLabel(iso, now)
  if (relative) {
    return relative.toUpperCase()
  }
  return formatTaskDate(iso).toUpperCase().replace(',', ' ·')
}

/**
 * The multi-campus day header: the dated form always, with TODAY / YESTERDAY
 * in front of it (`TODAY · FRI · SEP 18`), because a header over several
 * campus cards is the only place the date appears.
 */
export function formatDayGroupLabel(iso: string, now: Date = new Date()): string {
  const dated = formatTaskDate(iso).toUpperCase().replace(',', ' ·')
  const relative = relativeDayLabel(iso, now)
  return relative ? `${relative.toUpperCase()} · ${dated}` : dated
}

/** DOM id of a day's timeline section — the calendar's click target. */
export function daySectionId(serviceDate: string): string {
  return `production-day-${serviceDate}`
}

/**
 * The wizard header's second line: which campus and which service day the
 * task refers to — "Hall Middle School · Wed, Aug 19". The fullscreen wizard
 * hides the page underneath it, so without this an admin with several
 * campuses, or one arriving from a Tasks deep link, cannot tell which record
 * they are filling. Same `{campus} · {date}` shape as the serve header.
 */
export function taskContextLabel(locationName: string, serviceDate: string): string {
  return `${locationName} · ${formatTaskDate(serviceDate)}`
}

/**
 * The PHONE record wizard's header line, date first (PROD-1511): "Wed, Aug 19
 * · Hall Middle School". The header truncates its second line at about half a
 * phone's width, and a long campus name cut the day off; the day is what
 * tells two records apart, so on a phone it leads. Desktop keeps
 * `taskContextLabel`.
 */
export function taskDateFirstLabel(locationName: string, serviceDate: string): string {
  return `${formatTaskDate(serviceDate)} · ${locationName}`
}
